import { computed, ref, shallowRef } from 'vue';
import { ApiError, request } from './api';

/**
 * Who is signed in, as the SPA understands it.
 *
 * Everything here is advisory. The API decides authorization on every request
 * and this store cannot loosen that — its job is to avoid offering an action
 * that would be refused, which is a courtesy to the user rather than a
 * control.
 */

export interface Identity {
	readonly email: string;
	/** Absent unless they have set one; the address is the fallback label. */
	readonly displayName?: string;
	readonly roles: readonly string[];
	/**
	 * When the fresh window closes, in epoch ms.
	 *
	 * The API sends a boolean too, and it is deliberately not kept: a tab open
	 * for an hour would still be holding `fresh: true` from the moment it
	 * loaded, which is exactly when it stopped being true.
	 */
}

export type SessionState =
	/** Not asked yet. Distinct from anonymous: the shell must not flash sign-in. */
	| { readonly status: 'unknown' }
	| { readonly status: 'anonymous' }
	| { readonly status: 'signed-in'; readonly identity: Identity };

const state = shallowRef<SessionState>({ status: 'unknown' });

/**
 * Freshness is a deadline, so anything derived from it has to be re-evaluated
 * as time passes rather than computed once. Half a minute is well inside the
 * thirty the window allows.
 */
const TICK_MS = 30_000;
const now = shallowRef(Date.now());
let ticker: ReturnType<typeof setInterval> | undefined;

function startTicking(): void {
	ticker ??= setInterval(() => {
		now.value = Date.now();
	}, TICK_MS);
}

function stopTicking(): void {
	if (ticker !== undefined) clearInterval(ticker);
	ticker = undefined;
}

export const session = computed<SessionState>(() => state.value);

export const identity = computed<Identity | undefined>(() =>
	state.value.status === 'signed-in' ? state.value.identity : undefined,
);

export const isSignedIn = computed<boolean>(() => state.value.status === 'signed-in');

/**
 * What this deployment calls itself.
 *
 * Carried on the session bootstrap rather than read from the admin settings
 * route, because every signed-in user renders the header and almost none of
 * them are admins. The neutral default stands until somebody changes it
 * (`D26`), and an older API that sends nothing keeps it.
 */
/**
 * Neutral until somebody changes it (`D26`), and what stands when the API
 * cannot say — a deployment that will not name itself is still one somebody is
 * signing in to.
 */
const DEFAULT_NAME = 'AWS Dashboard';

const name = ref(DEFAULT_NAME);
export const siteName = computed<string>(() => name.value);

/**
 * The name, without a session (`D146`).
 *
 * For the sign-in page, which is reached by definition without one. A failure
 * leaves the neutral default standing — a deployment that cannot say its name
 * is still a deployment somebody is trying to sign in to.
 */
export async function loadDeploymentName(): Promise<void> {
	const answer = await request<{ siteName?: string }>('/api/deployment').catch(() => undefined);
	if (answer?.siteName !== undefined && answer.siteName !== '') name.value = answer.siteName;
}

/**
 * Exact membership, with no implication anywhere.
 *
 * `core:admin` is not a superuser: it manages people, not resources, and an
 * admin who needs to change a client's SES configuration holds `ses:operator`
 * too. The API decides it the same way — if this function were more generous,
 * the nav would offer links the API then refuses.
 */
export function hasRole(role: string): boolean {
	return identity.value?.roles.includes(role) ?? false;
}

/** True once the first `/api/me` has settled, either way. */
export const isResolved = computed<boolean>(() => state.value.status !== 'unknown');

interface MeResponse {
	readonly siteName?: string;
	readonly email: string;
	readonly displayName?: string;
	readonly roles: readonly string[];
}

/**
 * What to call the signed-in user: their name if they have set one, their
 * address otherwise. One place, so the header, the menu and the profile page
 * cannot disagree about it.
 */
export const callMe = computed<string>(
	() => identity.value?.displayName ?? identity.value?.email ?? '',
);

/**
 * Asks the API who the caller is. A 401 of any kind is simply "not signed in"
 * here — the distinction between an absent, expired and revoked session
 * matters to the audit log, not to a browser that has to show a sign-in form
 * in all three cases.
 */
export async function refresh(): Promise<SessionState> {
	try {
		const me = await request<MeResponse>('/api/me');
		if (me.siteName !== undefined && me.siteName !== '') name.value = me.siteName;

		state.value = {
			status: 'signed-in',
			identity: {
				email: me.email,
				...(me.displayName === undefined ? {} : { displayName: me.displayName }),
				roles: me.roles,
			},
		};
		now.value = Date.now();
		startTicking();
	} catch (error) {
		if (error instanceof ApiError && error.status === 401) {
			state.value = { status: 'anonymous' };
			stopTicking();
		} else {
			// A network failure or a 500 is not evidence of being signed out,
			// and treating it as such would bounce someone to sign-in over a
			// blip and lose whatever they were doing.
			throw error;
		}
	}
	return state.value;
}

/** Resolves the session once, and reuses that result for concurrent callers. */
let pending: Promise<SessionState> | undefined;

export function ensureResolved(): Promise<SessionState> {
	if (state.value.status !== 'unknown') return Promise.resolve(state.value);
	pending ??= refresh().finally(() => {
		pending = undefined;
	});
	return pending;
}

export async function signOut(): Promise<void> {
	// The cookie is cleared by the API's `Set-Cookie`; this store is only
	// catching up with what the browser has already been told.
	await request<void>('/api/auth/logout', { method: 'POST' });
	state.value = { status: 'anonymous' };
	stopTicking();
}

/** Sends the magic link. Answers identically for a known and unknown address. */
export async function requestSignIn(email: string, next?: string): Promise<void> {
	await request<{ accepted: boolean }>('/api/auth/request', {
		method: 'POST',
		body: next === undefined ? { email } : { email, next },
	});
}

/** Tests only: the store is module state, and a test must not inherit another's. */
export function resetSession(): void {
	state.value = { status: 'unknown' };
	pending = undefined;
	stopTicking();
	now.value = Date.now();
	// The name is module state too, and a test that inherited another's would
	// pass while asserting the wrong deployment's name.
	name.value = DEFAULT_NAME;
}

/**
 * Saves the display name and folds the answer back into the session, so the
 * header changes with the form rather than on the next page load.
 */
export async function setDisplayName(displayName: string): Promise<void> {
	const updated = await request<MeResponse>('/api/me/profile', {
		method: 'PUT',
		body: { displayName },
	});

	const current = identity.value;
	if (current === undefined) return;

	// The key is dropped rather than set to `undefined`: `Identity` declares
	// `displayName` optional under `exactOptionalPropertyTypes`, where a
	// present-but-undefined property is a different thing from an absent one —
	// and "cleared" has to mean absent, the same as it does in the store.
	const { displayName: _cleared, ...rest } = current;

	state.value = {
		status: 'signed-in',
		identity:
			updated.displayName === undefined
				? rest
				: { ...rest, displayName: updated.displayName },
	};
}
